import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  WhatsAppCampaignAPI,
  type WhatsAppSendResponse,
} from "src/services/WhatsAppCampaignAPI";
import { ApiError } from "src/services/HttpUtils";
import { setCampaignStatus } from "src/services/ChatbotAPI";
import { getThrownApiDetail, showApiDetailToast } from "src/utils/showApiDetailToast";
import type { ScheduleSelection } from "../../EmailCampaign/AiEmail/hooks/UseCampaignDelivery";
import {
  BYTES_PER_MB,
  MEDIA_RULES,
  WHATSAPP_CAMPAIGN_STEPS,
  WHATSAPP_EDITOR_KEY,
} from "../WhatsAppCampaignConstants";
import type {
  WhatsAppMediaAttachment,
  WhatsAppMediaState,
  WhatsAppTemplate,
  WhatsAppVariableDraft,
  WhatsAppVariableDrafts,
} from "../WhatsAppCampaignTypes";
import {
  buildVariableMappings,
  createEmptyDraft,
  getInitialDrafts,
  hasAllVariableValues,
  readImageDimensions,
} from "../WhatsAppCampaignUtils";
import { useWhatsAppAudience } from "./UseWhatsAppAudience";
import { useWhatsAppPreview } from "./UseWhatsAppPreview";

const EDITOR_STEP_INDEX = WHATSAPP_CAMPAIGN_STEPS.findIndex(step => step.key === "editor");
const ERRORS_KEY = `${WHATSAPP_EDITOR_KEY}.errors`;
const USER_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const TEMPLATE_UNAVAILABLE_CODES = [
  "template_not_found",
  "template_not_approved",
  "template_already_used",
];
const MEDIA_ERROR_CODES = ["header_media_required", "header_media_invalid"];

interface DeliveryStatus {
  type: "sent" | "scheduled";
  date?: string;
  time?: string;
}

interface UseWhatsAppCampaignWizardParams {
  listNames: string[];
  campaignName: string;
  onTemplatesStale: () => void;
}

export const useWhatsAppCampaignWizard = ({
  listNames,
  campaignName,
  onTemplatesStale,
}: UseWhatsAppCampaignWizardParams) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [template, setTemplate] = useState<WhatsAppTemplate | null>(null);
  const [drafts, setDrafts] = useState<WhatsAppVariableDrafts>({});
  const [attachment, setAttachment] = useState<WhatsAppMediaAttachment | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus | null>(null);
  const mediaUrlRef = useRef<string | null>(null);
  const mediaRequestRef = useRef(0);

  const stepKey = WHATSAPP_CAMPAIGN_STEPS[stepIndex].key;
  const isEditorStep = stepKey === "editor";

  const mappings = useMemo(
    () => (template ? buildVariableMappings(template, drafts, USER_TIME_ZONE) : []),
    [drafts, template]
  );
  const audience = useWhatsAppAudience(listNames, isEditorStep);
  const preview = useWhatsAppPreview(isEditorStep ? template : null, listNames, mappings);

  const revokeMediaUrl = useCallback(() => {
    if (mediaUrlRef.current) {
      URL.revokeObjectURL(mediaUrlRef.current);
      mediaUrlRef.current = null;
    }
  }, []);

  const discardMedia = useCallback(() => {
    mediaRequestRef.current += 1;
    revokeMediaUrl();
    setAttachment(null);
    setMediaError(null);
    setIsUploading(false);
  }, [revokeMediaUrl]);

  useEffect(
    () => () => {
      mediaRequestRef.current += 1;
      revokeMediaUrl();
    },
    [revokeMediaUrl]
  );

  const selectTemplate = useCallback(
    (nextTemplate: WhatsAppTemplate) => {
      if (nextTemplate.id !== template?.id) {
        setTemplate(nextTemplate);
        setDrafts(getInitialDrafts(nextTemplate));
        discardMedia();
      }
      setStepIndex(EDITOR_STEP_INDEX);
    },
    [discardMedia, template?.id]
  );

  const updateDraft = useCallback((key: string, draft: Partial<WhatsAppVariableDraft>) => {
    setDrafts(current => ({ ...current, [key]: { ...(current[key] ?? createEmptyDraft()), ...draft } }));
  }, []);

  const attachMedia = useCallback(
    async (file: File) => {
      const mediaType = template?.mediaType;
      if (!mediaType) {
        return;
      }
      const { accept, maxSizeMb } = MEDIA_RULES[mediaType];
      if (!accept.includes(file.type)) {
        setMediaError(t(`${ERRORS_KEY}.mediaType.${mediaType}`));
        return;
      }
      if (file.size > maxSizeMb * BYTES_PER_MB) {
        setMediaError(t(`${ERRORS_KEY}.mediaSize`, { size: maxSizeMb }));
        return;
      }

      const requestId = ++mediaRequestRef.current;
      const previewUrl = mediaType === "image" ? URL.createObjectURL(file) : null;
      setMediaError(null);
      setIsUploading(true);
      try {
        const dimensions = previewUrl ? await readImageDimensions(previewUrl) : null;
        const response = await WhatsAppCampaignAPI.uploadMedia(mediaType, file);
        if (!response.success || !response.media) {
          throw new ApiError(response.message ?? "");
        }
        if (requestId !== mediaRequestRef.current) {
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
          return;
        }
        revokeMediaUrl();
        mediaUrlRef.current = previewUrl;
        setAttachment({
          uploaded: response.media,
          fileName: response.media.file_name || file.name,
          sizeBytes: response.media.size_bytes ?? file.size,
          previewUrl,
          width: dimensions?.width ?? null,
          height: dimensions?.height ?? null,
        });
      } catch (error) {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        if (requestId === mediaRequestRef.current) {
          setMediaError(
            error instanceof ApiError && error.message ? error.message : t(`${ERRORS_KEY}.mediaUpload`)
          );
        }
      } finally {
        if (requestId === mediaRequestRef.current) {
          setIsUploading(false);
        }
      }
    },
    [revokeMediaUrl, t, template?.mediaType]
  );

  const media: WhatsAppMediaState = useMemo(
    () => ({ attachment, isUploading, error: mediaError }),
    [attachment, isUploading, mediaError]
  );

  const isComplete = useMemo(
    () =>
      template !== null &&
      hasAllVariableValues(template, drafts) &&
      (!template.mediaType || Boolean(attachment)) &&
      preview.errors.length === 0 &&
      !isUploading &&
      !isSending,
    [attachment, drafts, isSending, isUploading, preview.errors.length, template]
  );

  const goNext = useCallback(() => {
    if (template) {
      setStepIndex(EDITOR_STEP_INDEX);
    }
  }, [template]);

  const goBack = useCallback(() => {
    if (stepIndex === 0) {
      navigate(-1);
      return;
    }
    setStepIndex(current => current - 1);
  }, [navigate, stepIndex]);

  const changeTemplate = useCallback(() => setStepIndex(0), []);

  const handleRejection = useCallback(
    (response: WhatsAppSendResponse) => {
      const code = response.error_code ?? "";
      const text =
        [response.message, ...(response.errors ?? [])].filter(Boolean).join(" ") ||
        t(`${ERRORS_KEY}.sendFailed`);
      message.error(text);
      if (TEMPLATE_UNAVAILABLE_CODES.includes(code)) {
        setStepIndex(0);
        onTemplatesStale();
      } else if (MEDIA_ERROR_CODES.includes(code)) {
        if (code === "header_media_invalid") {
          discardMedia();
        }
        setMediaError(text);
      }
    },
    [discardMedia, onTemplatesStale, t]
  );

  const deliver = useCallback(
    async (selection?: ScheduleSelection): Promise<boolean> => {
      if (!template) {
        return false;
      }
      setIsSending(true);
      try {
        const response = await WhatsAppCampaignAPI.sendCampaign({
          campaign_name: campaignName.trim(),
          template_name: template.name,
          language_code: template.language,
          list_names: listNames,
          variable_mappings: mappings,
          ...(template.mediaType &&
            attachment && {
              header_media: {
                type: attachment.uploaded.type,
                ref: attachment.uploaded.ref,
                mime_type: attachment.uploaded.mime_type,
                file_name: attachment.uploaded.file_name,
              },
            }),
          ...(selection && { scheduled_for: selection.sendAt }),
        });
        if (!response.success) {
          handleRejection(response);
          return false;
        }
        setCampaignStatus("completed");
        setDeliveryStatus(
          selection
            ? { type: "scheduled", date: selection.date, time: selection.time }
            : { type: "sent" }
        );
        return true;
      } catch (error) {
        const detail = getThrownApiDetail(error);
        if (detail) {
          showApiDetailToast(detail);
        } else {
          message.error(t(`${ERRORS_KEY}.sendFailed`));
        }
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [attachment, campaignName, handleRejection, listNames, mappings, t, template]
  );

  const sendNow = useCallback(() => {
    deliver();
  }, [deliver]);

  const schedule = useCallback(
    async (selection: ScheduleSelection) => {
      if (await deliver(selection)) {
        setIsScheduleOpen(false);
      }
    },
    [deliver]
  );

  const openSchedule = useCallback(() => setIsScheduleOpen(true), []);
  const closeSchedule = useCallback(() => setIsScheduleOpen(false), []);
  const clearDeliveryStatus = useCallback(() => setDeliveryStatus(null), []);

  const reset = useCallback(() => {
    setStepIndex(0);
    setTemplate(null);
    setDrafts({});
    discardMedia();
    setIsScheduleOpen(false);
    setDeliveryStatus(null);
  }, [discardMedia]);

  return {
    stepIndex,
    stepKey,
    template,
    drafts,
    media,
    audience,
    preview,
    isComplete,
    isSending,
    isScheduleOpen,
    deliveryStatus,
    selectTemplate,
    updateDraft,
    attachMedia,
    removeMedia: discardMedia,
    goNext,
    goBack,
    changeTemplate,
    sendNow,
    schedule,
    openSchedule,
    closeSchedule,
    clearDeliveryStatus,
    reset,
  };
};
